import type { ADFAttribute } from './specs/types';
import type { AdfAttrValue } from './types';

/**
 * Generate default value for an attribute based on its rule.
 * Return null if we can't provide default value.
 */
export function generateDefaultValueForAttribute(
	attrRule: ADFAttribute,
	attrName: string,
	markOrNodeType: string,
): string | number | false | never[] | null {
	if (attrRule.type === 'enum' && attrRule.values && attrRule.values.length > 0) {
		return attrRule.values[0];
	}

	if (attrRule.type === 'number') {
		return attrRule.minimum ?? 0;
	}

	if (attrRule.type === 'string') {
		// If validatorFn is defined, can't provide default
		if (attrRule.validatorFn) {
			return null; // Signal that we can't provide default
		}

		// If pattern is defined, try to match it
		if (attrRule.pattern) {
			// Handle common hex color patterns
			if (attrRule.pattern === '^#[0-9a-fA-F]{6}$') {
				// Using white gray as default color for text
				if (markOrNodeType === 'textColor') {
					return '#172B4D';
				}
				// Using light gray as default color for background
				if (markOrNodeType === 'backgroundColor') {
					return '#DCDFE4';
				}
				// Using black as default color
				return '#000000';
			}

			// Only border has below regex pattern
			if (attrRule.pattern === '^#[0-9a-fA-F]{8}$|^#[0-9a-fA-F]{6}$') {
				// Using subtle gray as default border color
				return '#091E4224';
			}

			if (attrName === 'timestamp') {
				return new Date().getTime().toString();
			} else if (attrName === 'shortName') {
				return ':smile:';
			}
			/**
			 * For other patterns, return empty string
			 * Better solution is to use https://github.com/wimpyprogrammer/regex-to-strings
			 * 	and generate one string that matches the pattern
			 */
			return null;
		}

		// If minLength is defined, generate string of minimum length
		if (attrRule.minLength && attrRule.minLength > 0) {
			return 'a'.repeat(attrRule.minLength);
		}

		return null;
	}

	if (attrRule.type === 'boolean') {
		return false;
	}

	if (attrRule.type === 'array') {
		if (attrRule.minItems && attrRule.minItems > 0) {
			/**
			 * Generating default value which is useful is not possible
			 * 	based on current validation rules.
			 * So just returning null.
			 */
			return null;
		}
		return [];
	}

	return null;
}

/**
 * Validate a single attribute value
 */
export function validateAndRepairAttributeValue(
	attrName: string,
	value: AdfAttrValue,
	rule: ADFAttribute,
	markOrNodeType: string,
): { error?: string; isValid: boolean; repairedValue?: AdfAttrValue } {
	if (rule.type === 'string') {
		if (rule.pattern) {
			const regex = new RegExp(rule.pattern, 'u');
			if (!regex.test(value as string) && !rule.optional) {
				const defaultedValue = generateDefaultValueForAttribute(rule, attrName, markOrNodeType);
				if (defaultedValue === null) {
					return {
						isValid: false,
						error: `Attribute '${attrName}' does not match required pattern`,
					};
				}
				return { isValid: true, repairedValue: defaultedValue };
			}
			return { isValid: true, repairedValue: value };
		}

		// Only number value is allowed to be converted to string
		const stringValue =
			typeof value === 'string' || typeof value === 'number' ? value.toString() : undefined;
		if (stringValue && (rule.minLength ? stringValue.length > rule.minLength : true)) {
			return { isValid: true, repairedValue: stringValue };
		} else if (!rule.optional) {
			/**
			 * Right now default value is only generated for string type having pattern.
			 * Still calling generateDefaultValueForAttribute, so that in future we can generate default value
			 * 	for other cases, it will be supported automatically.
			 */
			const defaultedValue = generateDefaultValueForAttribute(rule, attrName, markOrNodeType);
			if (defaultedValue !== null) {
				return { isValid: true, repairedValue: defaultedValue };
			}
		}

		return {
			isValid: false,
			error: `Attribute '${attrName}' has invalid value and can't be repaired`,
		};
	}

	if (rule.type === 'number') {
		const repairedValue = typeof value === 'number' ? value : parseInt(value as string);

		/**
		 * if mininum or maximum is defined, and repaired value is not number,
		 * 	then set repaired value to minimum or maximum
		 * 	or if repaired value is number and out of range, then set repaired value to minimum or maximum
		 */
		if (rule.minimum !== undefined && (isNaN(repairedValue) || repairedValue < rule.minimum)) {
			return { isValid: true, repairedValue: rule.minimum };
		} else if (
			rule.maximum !== undefined &&
			(isNaN(repairedValue) || repairedValue > rule.maximum)
		) {
			return { isValid: true, repairedValue: rule.maximum };
		} else if (!isNaN(repairedValue)) {
			return { isValid: true, repairedValue: repairedValue };
		}

		/**
		 * For layoutColumn width, set default value to 20 in case of invalid value.
		 * Choose 20 as default value because it's the default width for 5 column layout,
		 * 	which is minium width for layoutColumn.
		 *
		 */
		if (markOrNodeType === 'layoutColumn' && attrName === 'width') {
			return { isValid: true, repairedValue: 20 };
		}

		return { isValid: false, error: `Failed to repair attribute '${attrName}'` };
	}

	if (rule.type === 'boolean') {
		if (typeof value === 'boolean') {
			return { isValid: true, repairedValue: value };
		} else if (typeof value === 'string') {
			if ('true'.includes(value)) {
				return { isValid: true, repairedValue: true };
			}
			if ('false'.includes(value)) {
				return { isValid: true, repairedValue: false };
			}
		}
		return {
			isValid: false,
			error: `Attribute '${attrName}' has invalid boolean value '${value}'`,
		};
	}

	if (rule.type === 'enum' && rule.values) {
		const enumValue = value as string;
		if (rule.values.includes(enumValue)) {
			return { isValid: true, repairedValue: value };
		} else {
			/**
			 * During streaming enum value may be incomplete, so try to find partial match
			 */
			const partialMatches = rule.values.filter((allowedValue) =>
				allowedValue.startsWith(enumValue),
			);
			if (partialMatches.length > 0) {
				return { isValid: true, repairedValue: partialMatches[0] };
			}
			/**
			 * If no partial match found, then set first value as default
			 */
			const defaultEnumValue = rule.values.includes('default') ? 'default' : rule.values[0];
			return { isValid: true, repairedValue: defaultEnumValue };
		}
	}

	if (rule.type === 'array') {
		if (!Array.isArray(value)) {
			return { isValid: false, error: `Attribute '${attrName}' must be an array` };
		}
		const arrayItemType = rule.items?.[0]?.type;
		const invalidItemType = value.some((item) => typeof item !== arrayItemType);
		if (invalidItemType) {
			return {
				isValid: false,
				error: `Attribute '${attrName}' must have items of type ${arrayItemType}`,
			};
		}

		if (rule.minItems !== undefined && value.length < rule.minItems) {
			return {
				isValid: false,
				error: `Attribute '${attrName}' must have at least ${rule.minItems} items`,
			};
		}
	}

	if (rule.type === 'object' && typeof value !== 'object') {
		return { isValid: false, error: `Attribute '${attrName}' must be an object` };
	}

	return { isValid: true, repairedValue: value };
}
